import './fill-point.less';
import classNames from 'classnames';
import { isNull, isUndefined } from 'lodash';
import React from 'react';
import { Select } from '@meitu/saas-ui';
import { IBrushLayerProps, IBrushPoint } from '@meitu/saas-editor-core/es/version-types';
import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import { useGlobalWidget } from '@meitu/saas-editor-ui/es/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@meitu/saas-editor-ui/es/hooks/use-listen-layer-props';
import IconFont from '@meitu/xdesign-icon-font';
import { getBrushPointList } from '@/editor/point-config';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { isIntl } from '@/react-process-env';
import { useEditorModel } from '@/store/models/editor-model';

const { Option } = Select;

export const FillPointFeature: React.FC<{
    id?: string;
    startPoint?: IBrushPoint;
    endPoint?: IBrushPoint;
    onAfterChange?: Function;
    style?: React.CSSProperties;
}> = React.memo(({ style, id, startPoint, endPoint, onAfterChange }) => {
    const { $t } = useLocaleStore();
    const theme = useEditorModel((state) => state.theme);
    const needListener = !isUndefined(id);
    const selectGlobalWidgetRef = useGlobalWidget(editor, false);
    const res = useListenActivePickLayerProps(
        editor,
        (layer) => {
            if (!layer) return null;
            const { startPoint, endPoint } = layer.data as IBrushLayerProps;
            return { startPoint, endPoint };
        },
        true,
        needListener,
    );

    if (needListener) {
        if (isNull(res)) {
            return null;
        }
        startPoint = res.startPoint;
        endPoint = res.endPoint;
    } else {
        if (isUndefined(startPoint) || isUndefined(endPoint)) return null;
    }

    return (
        <div className="c-brush-panel__item" style={{ ...style }}>
            <LabelAction title={$t((v) => v.panel.brushPanel.endpoint)} align={true}>
                <div className="c-brush-panel__point">
                    <div style={isIntl ? { width: 56 } : { width: 70 }}>
                        <Select
                            onDropdownVisibleChange={(e) => {
                                selectGlobalWidgetRef.current = e;
                            }}
                            value={startPoint.type}
                            className="c-brush-panel__dasharray"
                            suffixIcon={
                                <IconFont name="ChevronDownBlack" fontSize={10} color="var(--content-input-label)" />
                            }
                            onChange={(value: any, option: any) => {
                                const { url, offset, width, height } = option;
                                if (id) {
                                    editor.brushLayer.setPoint(
                                        id,
                                        {
                                            url,
                                            type: value,
                                            offset,
                                            width,
                                            height,
                                        },
                                        true,
                                    );
                                }
                                onAfterChange?.(77, {
                                    operate: 'left',
                                    url,
                                    type: value,
                                    offset,
                                    width,
                                    height,
                                });
                            }}
                        >
                            {getBrushPointList().map((item, index) => {
                                return (
                                    <Option
                                        key={index}
                                        url={item.url}
                                        offset={item.offset}
                                        width={item.width}
                                        height={item.height}
                                        value={item.value}
                                    >
                                        <div
                                            className={classNames('c-brush-panel__img-wrap', {
                                                'c-brush-panel__img-wrap-intl': isIntl,
                                            })}
                                        >
                                            <img alt="" src={(item as any)[theme]} width={50} height={20} />
                                        </div>
                                    </Option>
                                );
                            })}
                        </Select>
                    </div>
                    <div
                        className="c-brush-panel__icon"
                        onClick={() => {
                            if (id) {
                                editor.brushLayer.swapPoint(id);
                            }
                            onAfterChange?.(77, { operate: 'swap' });
                        }}
                    >
                        <IconFont name="ReplaceBold" fontSize={16} color="var(--content-system-primary)" />
                    </div>
                    <div style={isIntl ? { width: 56 } : { width: 70 }}>
                        <Select
                            value={endPoint.type}
                            className="c-brush-panel__dasharray"
                            suffixIcon={
                                <IconFont name="ChevronDownBlack" fontSize={10} color="var(--content-input-label)" />
                            }
                            onChange={(value: any, option: any) => {
                                const { url, offset, width, height } = option;
                                if (id) {
                                    editor.brushLayer.setPoint(
                                        id,
                                        {
                                            url,
                                            type: value,
                                            offset,
                                            width,
                                            height,
                                        },
                                        false,
                                    );
                                }
                                onAfterChange?.(77, {
                                    operate: 'right',
                                    url,
                                    type: value,
                                    offset,
                                    width,
                                    height,
                                });
                            }}
                        >
                            {getBrushPointList().map((item, index) => {
                                return (
                                    <Option
                                        key={index}
                                        url={item.url}
                                        offset={item.offset}
                                        width={item.width}
                                        height={item.height}
                                        value={item.value}
                                    >
                                        <div
                                            className={classNames('c-brush-panel__img-wrap', {
                                                'c-brush-panel__img-wrap-intl': isIntl,
                                            })}
                                        >
                                            <img
                                                alt=""
                                                style={{
                                                    transform: 'rotateY(180deg)',
                                                }}
                                                src={(item as any)[theme]}
                                                width={50}
                                                height={20}
                                            />
                                        </div>
                                    </Option>
                                );
                            })}
                        </Select>
                    </div>
                </div>
            </LabelAction>
        </div>
    );
});
